import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import TsLogoWhite from '../assets/TSLogoWhite.svg';
import './setup.css';

const SetupSignIn = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { signIn, user } = UserAuth();

    const searchParams = new URLSearchParams(location.search || window.location.search);
    const mId = searchParams.get('machine_id');
    const prod = searchParams.get('product');

    useEffect(() => {
        if (mId) localStorage.setItem('setup_machine_id', mId);
        if (prod) localStorage.setItem('setup_product', prod);
    }, [mId, prod]);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await signIn(email, password);
            navigate(`/setup/licenses${location.search}`);
        } catch (error) {
            setIsLoading(false);
            switch (error.code) {
                case 'auth/wrong-password':
                    setError("Incorrect password. Please try again.");
                    break;
                case 'auth/user-not-found':
                    setError("No account found with that email address.");
                    break;
                case 'auth/invalid-email':
                    setError("Please enter a valid email address.");
                    break;
                default:
                    setError("Authentication failed. Please check your connection.");
                    break;
            }
        }
    };

    return (
        <div className="setup-app-container">
            {/* The glass panel sits above the animated background defined in setup.css */}
            <motion.div
                className="setup-glass-panel"
                initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: 10 }} 
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.3 }}
                style={{ perspective: 1000 }}
            >
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                    style={{ textAlign: 'center' }}
                >
                    <img src={TsLogoWhite} alt="Tape Signal" style={{ width: '78px', marginBottom: '5px', marginRight: '8px' }} />
                </motion.div>
                
                <motion.h1 
                    className="setup-header"
                    initial={{ opacity: 0, filter: 'blur(10px)' }} 
                    animate={{ opacity: 1, filter: 'blur(0px)' }} 
                    transition={{ delay: 0.3, duration: 0.8 }}
                >
                    Welcome.
                </motion.h1>
                <motion.p 
                    className="setup-subtitle"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                >
                    Authenticate to begin setup.
                </motion.p>

                <motion.form 
                    onSubmit={handleSignIn}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
                >
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="email"
                            placeholder="Email Address"
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => setFocusedInput('email')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>
                    <div className="setup-input-group">
                        <motion.input
                            className="setup-input"
                            type="password"
                            placeholder="Password"
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setFocusedInput('password')}
                            onBlur={() => setFocusedInput(null)}
                            required
                        />
                    </div>

                    <motion.button
                        type="submit"
                        className="setup-btn-primary"
                        disabled={!email || !password || isLoading}
                    >
                        {isLoading ? <div className="setup-loader-ring"></div> : "Sign In"}
                    </motion.button>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                className="setup-error"
                                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                animate={{ opacity: 1, height: 'auto', marginTop: 24 }}
                                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.form>
            </motion.div>
        </div>
    );
};

export default SetupSignIn;
